import { type OptJobWithQueueItems, useOptJobsWithQueueItems } from 'api/optJobs/optJobs.api'
import type { QueueItem } from 'api/queueItems/queueItems.types'

const ELECTRICITY_PRICE_SCENARIO_JOB_TYPE = 'electricity_price_scenario'

export type ScenarioOptimizationPeriod = {
  periodStart: ISODateTime
  periodEnd: ISODateTime
  finishedAt: ISODateTime
}

function findQueueItemForSubtypes(job: OptJobWithQueueItems, subtypes: string[] | null): QueueItem | undefined {
  if (subtypes === null || subtypes.length === 0) {
    return job.queue_items.find((item) => item.opt_start_time && item.opt_end_time)
  }
  return job.queue_items.find(
    (item) => item.subtype != null && subtypes.includes(item.subtype) && item.opt_start_time && item.opt_end_time
  )
}

/**
 * Returns the actual optimization period and completion time of the scenario's
 * most recent finished electricity price scenario optimization, or null when
 * none has run.
 */
export function useLatestScenarioOptimizationPeriod(subtypes: string[] | null): ScenarioOptimizationPeriod | null {
  const { data: optJobs } = useOptJobsWithQueueItems({
    optJobTypes: [ELECTRICITY_PRICE_SCENARIO_JOB_TYPE],
    optJobStatuses: ['Finished'],
  })

  if (!optJobs?.length) {
    return null
  }

  let latest: ScenarioOptimizationPeriod | null = null
  for (const job of optJobs) {
    const queueItem = findQueueItemForSubtypes(job, subtypes)
    const finishedAt = queueItem?.finished_at ?? queueItem?.status_updated_at ?? job.status_updated_at
    if (queueItem?.opt_start_time && queueItem.opt_end_time && finishedAt) {
      // Response order is by created_at, so pick the latest completion explicitly.
      if (!latest || new Date(finishedAt).getTime() > new Date(latest.finishedAt).getTime()) {
        latest = {
          periodStart: queueItem.opt_start_time as ISODateTime,
          periodEnd: queueItem.opt_end_time as ISODateTime,
          finishedAt: finishedAt as ISODateTime,
        }
      }
    }
  }

  return latest
}
